import React from 'react';
import { AnimatePresence } from 'framer-motion';

// Atomic Modals
import { AbandonModal } from './modals/AbandonModal';
import { FinishModal } from './modals/FinishModal';
import { DeleteLogModal } from './modals/DeleteLogModal';
import { SleepConfirmModal } from './modals/SleepConfirmModal';
import { WakeUpConfirmModal } from './modals/WakeUpConfirmModal';
import { SessionConflictModal } from './modals/SessionConflictModal';
import { CloneConfirmModal } from './modals/CloneConfirmModal';

interface SessionModalsProps {
    showAbandonModal: boolean;
    setShowAbandonModal: (show: boolean) => void;
    handleAbandonSession: () => void;
    showFinishModal: boolean;
    setShowFinishModal: (show: boolean) => void;
    handleFinalizeSession: () => void;
    isSaving: boolean;
    globalPercent: number;
    confirmDeleteLog: { id: string; type: string } | null;
    setConfirmDeleteLog: (log: { id: string; type: string } | null) => void;
    handleDeleteLog: () => void;
    isDeleting: boolean;
    showSleepConfirm: boolean;
    setShowSleepConfirm: (show: boolean) => void;
    handleStartSleep: () => void;
    showWakeUpConfirm: boolean;
    setShowWakeUpConfirm: (show: boolean) => void;
    handleEndSleep: () => void;
    showSessionConflict: boolean;
    setShowSessionConflict: (show: boolean) => void;
    showCloneConfirm: { event: any, date: Date } | null;
    setShowCloneConfirm: (val: { event: any, date: Date } | null) => void;
    handleConfirmClone: () => void;
}

/**
 * SessionModals: Orchestrator for all global mission-critical dialogs.
 * REFACTORED: Decomposed into atomic sub-components for SoC and readability.
 */
export const SessionModals: React.FC<SessionModalsProps> = React.memo((props) => {
    const {
        showAbandonModal, setShowAbandonModal, handleAbandonSession,
        showFinishModal, setShowFinishModal, handleFinalizeSession, isSaving, globalPercent,
        confirmDeleteLog, setConfirmDeleteLog, handleDeleteLog, isDeleting,
        showSleepConfirm, setShowSleepConfirm, handleStartSleep,
        showWakeUpConfirm, setShowWakeUpConfirm, handleEndSleep,
        showSessionConflict, setShowSessionConflict,
        showCloneConfirm, setShowCloneConfirm, handleConfirmClone
    } = props;

    return (
        <AnimatePresence>
            {showAbandonModal && (
                <AbandonModal 
                    onClose={() => setShowAbandonModal(false)} 
                    onConfirm={handleAbandonSession} 
                />
            )}

            {showFinishModal && (
                <FinishModal 
                    onClose={() => setShowFinishModal(false)} 
                    onConfirm={handleFinalizeSession} 
                    isSaving={isSaving} 
                    globalPercent={globalPercent} 
                />
            )}

            {confirmDeleteLog && (
                <DeleteLogModal 
                    confirmDeleteLog={confirmDeleteLog} 
                    onClose={() => setConfirmDeleteLog(null)} 
                    onConfirm={handleDeleteLog} 
                    isDeleting={isDeleting} 
                />
            )}

            {showSleepConfirm && (
                <SleepConfirmModal 
                    onClose={() => setShowSleepConfirm(false)} 
                    onConfirm={handleStartSleep} 
                />
            )}

            {showWakeUpConfirm && (
                <WakeUpConfirmModal 
                    onClose={() => setShowWakeUpConfirm(false)} 
                    onConfirm={handleEndSleep} 
                />
            )}

            {showSessionConflict && (
                <SessionConflictModal 
                    onClose={() => setShowSessionConflict(false)} 
                />
            )}

            {showCloneConfirm && (
                <CloneConfirmModal 
                    event={showCloneConfirm.event} 
                    date={showCloneConfirm.date} 
                    onClose={() => setShowCloneConfirm(null)} 
                    onConfirm={handleConfirmClone} 
                    isSaving={isSaving} 
                />
            )}
        </AnimatePresence>
    );
});
